import { type Action, type AnyAction, type Dispatch, type ThunkDispatch } from '@reduxjs/toolkit';
import { io, type Socket } from 'socket.io-client';

import { Jwt, type JwtToken, parseJwt, TokenStatus } from '@pkg/classes';
import { refreshAuthToken } from '@pkg/hooks/authentication/utilities/refreshAuthToken';

import { setError } from '@app/store/actions/error';
import { type RootState } from '@app/store/reducers';
import { hide2faExpiredLogin, show2faExpiredLogin } from '@app/store/slices';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

import {
    type ApiResult,
    type ErrorResponse,
    type GqlEndpoint,
    type GraphQLErrorResponse,
    type PaginatedResult,
    type SimpleQuery,
} from '../common/structs';
import { links } from '../links';
import { refreshToken } from '../resources/w3auth';
import { token } from '../resources/websockets';

///
/// Types

interface WebsocketConnection {
    handler: (message: MessageEvent) => (dispatch: ThunkDispatch<RootState, void, Action>) => Promise<void>;
    maxBackOff: number;
    url: string;
    pathname?: string;
}

interface ApiWebsocketConnection extends WebsocketConnection {
    connection?: AutoConnectWebsocket;
}

interface YacsWebsocketConnection extends WebsocketConnection {
    connection?: Socket;
}

interface WebsocketConnections {
    api: ApiWebsocketConnection;
    yacs: YacsWebsocketConnection;
}

interface ApiCallOptions {
    publicRequest?: boolean;
}

interface ApiParams {
    url: string;
    wsUrl: string;
    timeout?: number;
    dispatch: Dispatch;
    wsHandler: (message: MessageEvent) => (dispatch: ThunkDispatch<RootState, void, Action>) => Promise<void>;
    wsMaxBackOff: number;
    websocketConnections: WebsocketConnections;
}

interface ApiWebsocket {
    initializeWebsocket: (incognito: boolean, authToken?: string) => Promise<void>;
    disconnectWebsocket: () => void;
}

interface ApiToken {
    getTokenStatus: (withinSeconds: number) => {
        refresh_token: TokenStatus;
        access_token: TokenStatus;
    };
}

interface ApiCall {
    call: <T, P>(endpoint: (params: P) => Promise<ApiResult<T>>, params: P) => Promise<ApiResult<T>>;
    graphqlCall: <T, P>(
        endpoint: (params: P) => Promise<T & { errors?: GraphQLErrorResponse }>,
        params: P,
    ) => Promise<T & { errors?: GraphQLErrorResponse }>;
}

export interface ParsedTokens {
    refresh_token?: JwtToken;
    access_token?: JwtToken;
}

///
/// Auto-connect Websocket

export class AutoConnectWebsocket {
    _ws?: WebSocket = undefined;
    _connected = false;
    _attemptReconnect = true;
    _wsRetryCount = 0;
    _wsUri = '';
    _incognito = false;
    _wsMaxBackOff = 32;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _onMessagesCallbacks: Record<string, (message: MessageEvent<any>) => void> = {};

    _onOpenCallbacks: Record<string, (ws: AutoConnectWebsocket) => void> = {};
    _connectionAttempts: Date[] = [];
    _connectionTimes: Date[] = [];

    constructor(wsUri: string, wsMaxBackOff: number, authToken?: string, incognito?: boolean) {
        this._wsUri = wsUri;
        this._incognito = incognito || false;
        this._wsMaxBackOff = wsMaxBackOff;

        void this.connect(authToken);
    }

    async connect(authToken?: string, incognito?: boolean) {
        if (incognito !== undefined) {
            this._incognito = incognito;
        }

        try {
            if (this._ws) this.closeWithoutReconnect();

            let wsToken = authToken || '';
            let wsAddress = this._wsUri;

            if (this._incognito) {
                wsAddress += links.ws().incognito();
            } else {
                if (!authToken) {
                    wsToken = (await this._fetchWebsocketToken()) || '';
                }

                wsAddress += `${links.ws().single()}?auth_token=${wsToken}`;
            }

            this._connectionAttempts.push(new Date());
            this._ws = new WebSocket(wsAddress);
            this._ws.onopen = this.onopen.bind(this);
            this._ws.onmessage = this.onMessage.bind(this);
            this._ws.onclose = this.reconnect.bind(this);
            this._ws.onerror = this.reconnect.bind(this);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }

    async reconnect() {
        if (!this.attemptReconnect) {
            return;
        }
        const timeout = Math.min(
            (2 ^ this._wsRetryCount) + Math.floor(Math.random() * 1000),
            this._wsMaxBackOff * 1000,
        );

        // eslint-disable-next-line no-console
        console.warn(`Attempting to reconnect websocket, backing off for ${timeout}ms`);

        this._wsRetryCount++;
        setTimeout(async () => {
            await this.connect(undefined);
        }, timeout);
    }

    closeWithoutReconnect() {
        this._attemptReconnect = false;
        this._ws?.close();
        this._ws = undefined;
    }

    onopen() {
        // eslint-disable-next-line no-console
        console.debug('Websocket connection open');

        this._wsRetryCount = 0;
        this._attemptReconnect = true;
        this._connected = true;
        this._connectionTimes.push(new Date());
        Object.values(this._onOpenCallbacks).forEach((cb) => {
            cb(this);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMessage(message: MessageEvent<any>) {
        Object.values(this._onMessagesCallbacks).forEach((onMessageCallback) => {
            onMessageCallback(message);
        });
    }

    async _fetchWebsocketToken() {
        const fetchToken = await apiFactory().call(token, { value: {} });
        if (fetchToken.ok) {
            return fetchToken.value.auth_token;
        }
        return false;
    }

    send(message: string) {
        this._ws?.send(message);
    }

    addOnOpen(key: string, onOpenCallback: (ws: AutoConnectWebsocket) => void) {
        this._onOpenCallbacks[key] = onOpenCallback;
    }

    removeOnOpen(key: string) {
        delete this._onOpenCallbacks[key];
    }

    addOnMessage(key: string, onMessage: (message: MessageEvent) => void) {
        this._onMessagesCallbacks[key] = onMessage;
    }

    removeOnMessage(key: string) {
        delete this._onMessagesCallbacks[key];
    }

    get attemptReconnect() {
        return this._attemptReconnect;
    }

    get connected() {
        return this._connected;
    }
}

///
/// Default API Token Checker

abstract class DefaultApiTokenChecker implements ApiToken {
    public token = () => useAuthTokens.getState().token;

    public getTokenStatus(withinSeconds = 30): { refresh_token: TokenStatus; access_token: TokenStatus } {
        return {
            refresh_token: this._checkToken(this.token()?.refresh_token, withinSeconds),
            access_token: this._checkToken(this.token()?.access_token, withinSeconds),
        };
    }

    protected _checkToken(token?: string, withinSeconds = 30): TokenStatus {
        try {
            if (!token) {
                return TokenStatus.invalid;
            }

            const jwt = parseJwt(token);
            const tokenExpiresAt = +jwt.exp * 1000 - withinSeconds * 1000;
            const expiryLimit = new Date().getTime();
            return tokenExpiresAt > expiryLimit ? TokenStatus.valid : TokenStatus.expired;
        } catch {
            return TokenStatus.invalid;
        }
    }
}

///
/// Global API Class

export class Api extends DefaultApiTokenChecker implements ApiWebsocket, ApiCall {
    url: string;
    wsUrl: string;
    timeout: number;
    dispatch: ThunkDispatch<RootState, void, AnyAction>;
    ws?: AutoConnectWebsocket;
    wsHandler: (message: MessageEvent) => void;
    wsMaxBackOff: number;
    websocketConnections: WebsocketConnections;

    constructor(params: ApiParams) {
        super();
        this.url = params.url;
        this.wsUrl = params.wsUrl;
        this.timeout = params.timeout || 30 * 1000;
        this.dispatch = params.dispatch;
        this.wsHandler = (message) => {
            void this.dispatch(params.wsHandler(message));
        };
        this.wsMaxBackOff = params.wsMaxBackOff;
        this.websocketConnections = params.websocketConnections;
    }

    ///
    /// Websocket Connections

    // Initialize Websocket connections
    async initializeWebsocket(incognito: boolean, websocketAuthToken?: string, userAuthToken?: string) {
        await this.initializeApiWebsocket(incognito, websocketAuthToken);
        if (userAuthToken) {
            await this.initializeYacsWebsocket();
        }
    }

    async initializeApiWebsocket(incognito: boolean, authToken?: string) {
        if (!this.websocketConnections.api.connection) {
            this.websocketConnections.api.connection = new AutoConnectWebsocket(
                this.websocketConnections.api.url,
                this.websocketConnections.api.maxBackOff,
                authToken,
                incognito,
            );
            this.websocketConnections.api.connection.addOnMessage('primary', (msg) => {
                this.websocketConnections.api.handler(msg)(this.dispatch);
            });
        } else {
            await this.websocketConnections.api.connection.connect(authToken, incognito);
        }
    }

    async initializeYacsWebsocket() {
        if (!this.websocketConnections.yacs.connection) {
            this.websocketConnections.yacs.connection = io(this.websocketConnections.yacs.url, {
                path: this.websocketConnections.yacs.pathname,
                transports: ['websocket'],
            });
        }

        this.websocketConnections.yacs.connection.on('connect_error', (error) => {
            // eslint-disable-next-line no-console
            console.log(`Socket connection error: ${error.message}`);
        });
    }

    // Disconnect Websocket connections.
    disconnectWebsocket() {
        this.disconnectApiWebsocket();
        this.disconnectYacsWebsocket();
    }

    disconnectApiWebsocket() {
        this.websocketConnections.api.connection?.closeWithoutReconnect();
    }

    disconnectYacsWebsocket() {
        this.websocketConnections.yacs.connection?.disconnect();
    }

    async getToken<T, P>(endpoint: (params: P) => Promise<ApiResult<T> | T>, options?: ApiCallOptions) {
        if (
            endpoint.toString() !== refreshToken.toString() &&
            this.getTokenStatus().access_token === TokenStatus.expired
        ) {
            await refreshAuthToken(this.dispatch);
        }

        // If public request is forced ignore the auth token.
        return this.token() && !options?.publicRequest ? this.token() : undefined;
    }

    async handle2fa() {
        const token = this.token();
        const jwt = new Jwt(token?.access_token);
        const expired = jwt.is2FAExpired();
        if (expired) {
            this.dispatch(
                show2faExpiredLogin((token) => {
                    if (token) {
                        this.dispatch(hide2faExpiredLogin());
                    }
                }),
            );
        } else {
            this.dispatch(hide2faExpiredLogin());
        }
    }

    async call<T, P>(
        endpoint: (params: P) => Promise<ApiResult<T>>,
        params: P,
        options?: ApiCallOptions,
    ): Promise<ApiResult<T>> {
        const token = await this.getToken(endpoint, options);

        params = params || ({} as P);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const settings = (params as any).settings || {};

        return endpoint({
            ...params,
            token,
            settings: {
                url: this.url,
                ...settings,
            },
        }).then((result) => {
            if (!result.ok) {
                const twoFAExpired =
                    result.status.code == 424 ||
                    String(result.error) === 'Unauthorized: Invalid token: missing required 2FA' ||
                    String(result.error) === '2FA expired: 2FA token has expired';
                if (twoFAExpired) {
                    this.handle2fa();
                }
            }
            return result;
        });
    }

    async graphqlCall<T, P>(
        endpoint: GqlEndpoint<T, P>,
        params: P,
        options?: ApiCallOptions,
    ): Promise<T & { errors?: GraphQLErrorResponse }> {
        const token = await this.getToken(endpoint, options);
        params = params || ({} as P);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const settings = (params as any).settings || {};

        return endpoint({
            ...params,
            token,
            settings: {
                url: this.url,
                ...settings,
            },
        });
    }
}

///
/// Global API instantiation + access

let api: Api;

export function buildApi(apiParams: ApiParams): Api {
    api = new Api(apiParams);
    return api;
}

export function apiFactory(): Api {
    return api;
}

///
/// API return helpers
// standard
// TODO: fix types - create generic types for `apiCall` & 'params' so the args can be typed
export function apiReturnResults<T, ET = ErrorResponse>(
    apiCall,
    params,
    publicRequest = false,
): Promise<ApiResult<T, ET>> {
    return apiFactory()
        .call(apiCall, params, { publicRequest })
        .then((result) => {
            return result;
        })
        .catch((error) => error) as unknown as Promise<ApiResult<T, ET>>;
}

export function apiReturnPaginatedResults<T, R = PaginatedResult<T>>(
    apiCall,
    params,
    publicRequest = false,
): Promise<R> {
    return apiFactory()
        .call(apiCall, params, { publicRequest })
        .then((result) => {
            if (result?.ok) {
                return result.value;
            }
        })
        .catch((error) => {
            setError({
                ...error,
                log: false,
            });

            return error;
        }) as unknown as Promise<R>;
}

// gql

export async function graphqlReturnResults<T, P = SimpleQuery>(gqlCall: GqlEndpoint<T, P>, params: P) {
    return apiFactory()
        .graphqlCall(gqlCall, params)
        .then((r) => {
            const errors = r?.errors;
            const error = errors?.response.errors?.[0];
            if (error) {
                // setError({
                //     error: error.message,
                //     source: error.path?.toString(),
                //     log: true,
                // });
            }
            return {
                ...r,
                error,
            };
        })
        .catch((e) => {
            // eslint-disable-next-line no-console
            console.error(e);
            throw e;
        });
}
